// AI-GEN-BEGIN - 路由 Store 优化：清理废弃代码、提取重复逻辑、统一异步风格
import { cloneDeep, isString } from 'lodash-es'
import type { RouteMeta, RouteRecordRaw } from 'vue-router'
import useSettingsStore from './settings'
import useUserStore from './user'
import { resolveRoutePath } from '@/utils'
import { systemRoutes, constantRoutes } from '@/router/routes'
import apiApp from '@/api/modules/app'
import { getMenuList } from '@/api/modules/user'
import type { Route } from '@/types/global'
import { componentsMap } from '@/vendor/componentsMap'

const useRouteStore = defineStore(
  'route',
  () => {
    const settingsStore = useSettingsStore()
    const userStore = useUserStore()

    const isGenerate = ref(false)
    const routesRaw = ref<Route.recordMainRaw[]>([])
    const filesystemRoutesRaw = ref<RouteRecordRaw[]>([])
    const currentRemoveRoutes = ref<(() => void)[]>([])

    // ===================== 路由扁平化（三级及以上 → 二级） =====================

    function flatAsyncRoutes<T extends RouteRecordRaw>(route: T): T {
      if (route.children) {
        route.children = flatAsyncRoutesRecursive(route.children, [{
          path: route.path,
          title: route.meta?.title,
          hide: !route.meta?.breadcrumb && route.meta?.breadcrumb === false,
        }], route.path, route.meta?.auth)
      }
      return route
    }

    function flatAsyncRoutesRecursive(
      routes: RouteRecordRaw[],
      breadcrumb: Route.breadcrumb[] = [],
      baseUrl = '',
      baseAuth: RouteMeta['auth'],
    ): RouteRecordRaw[] {
      const res: RouteRecordRaw[] = []
      routes.forEach((route) => {
        if (route.children && route.children.length) {
          const childrenBaseUrl = resolveRoutePath(baseUrl, route.path)
          const childrenBaseAuth = baseAuth ?? route.meta?.auth
          const tmpBreadcrumb = cloneDeep(breadcrumb)
          tmpBreadcrumb.push({
            path: childrenBaseUrl,
            title: route.meta?.title,
            hide: !route.meta?.breadcrumb && route.meta?.breadcrumb === false,
          })
          const tmpRoute = cloneDeep(route)
          tmpRoute.path = childrenBaseUrl
          if (!tmpRoute.meta) tmpRoute.meta = {}
          tmpRoute.meta.auth = childrenBaseAuth
          tmpRoute.meta.breadcrumbNeste = tmpBreadcrumb
          delete tmpRoute.children
          res.push(tmpRoute)
          const childrenRoutes = flatAsyncRoutesRecursive(route.children, tmpBreadcrumb, childrenBaseUrl, childrenBaseAuth)
          childrenRoutes.forEach((item) => {
            const existIdx = res.findIndex(v => v.path === item.path)
            if (existIdx >= 0) {
              res[existIdx] = item
            } else {
              res.push(item)
            }
          })
        } else {
          const tmpRoute = cloneDeep(route)
          tmpRoute.path = resolveRoutePath(baseUrl, tmpRoute.path)
          const tmpBreadcrumb = cloneDeep(breadcrumb)
          tmpBreadcrumb.push({
            path: tmpRoute.path,
            title: tmpRoute.meta?.title,
            hide: !tmpRoute.meta?.breadcrumb && tmpRoute.meta?.breadcrumb === false,
          })
          if (!tmpRoute.meta) tmpRoute.meta = {}
          if (typeof tmpRoute.meta === 'string') {
            tmpRoute.meta = JSON.parse(tmpRoute.meta)
          }
          tmpRoute.meta!.auth = baseAuth ?? tmpRoute.meta?.auth
          tmpRoute.meta!.breadcrumbNeste = tmpBreadcrumb
          res.push(tmpRoute)
        }
      })
      return res
    }

    // ===================== 计算属性 =====================

    const flatRoutes = computed(() => {
      const returnRoutes: RouteRecordRaw[] = []
      if (!routesRaw.value) return returnRoutes

      if (settingsStore.settings.app.routeBaseOn !== 'filesystem') {
        routesRaw.value.forEach((item) => {
          if (item.children && Object.keys(item.children).length) {
            const tmpRoutes = cloneDeep(item.children) as RouteRecordRaw[]
            tmpRoutes.forEach((v) => {
              if (!v.meta) v.meta = {}
              v.meta.auth = item.meta?.auth ?? v.meta?.auth
            })
            returnRoutes.push(...tmpRoutes)
          }
        })
        returnRoutes.forEach(item => flatAsyncRoutes(item))
      } else {
        returnRoutes.push(...cloneDeep(filesystemRoutesRaw.value))
      }
      return returnRoutes
    })

    const flatSystemRoutes = computed(() => {
      const routes = [...systemRoutes]
      routes.forEach(item => flatAsyncRoutes(item))
      return routes
    })

    // ===================== 权限过滤 =====================

    function hasPermission(permissions: string[], route: Route.recordMainRaw | RouteRecordRaw) {
      if (!route.meta?.auth) return true
      return permissions.some((auth) => {
        if (typeof route.meta?.auth === 'string') {
          return route.meta.auth === '' || route.meta.auth === auth
        }
        if (typeof route.meta?.auth === 'object') {
          return route.meta.auth.length === 0 || route.meta.auth.includes(auth)
        }
        return false
      })
    }

    function filterAsyncRoutes<T extends Route.recordMainRaw[] | RouteRecordRaw[]>(routes: T, permissions: string[]): T {
      const res: any = []
      routes.forEach((route) => {
        if (hasPermission(permissions, route)) {
          const tmpRoute = cloneDeep(route)
          if (tmpRoute.children) {
            tmpRoute.children = filterAsyncRoutes(tmpRoute.children, permissions)
            if (tmpRoute.children.length) res.push(tmpRoute)
          } else {
            res.push(tmpRoute)
          }
        }
      })
      return res
    }

    const routes = computed(() => {
      if (settingsStore.settings.app.enablePermission) {
        return filterAsyncRoutes(routesRaw.value as any, userStore.permissions)
      }
      return cloneDeep(routesRaw.value) as any as Route.recordMainRaw[]
    })

    // ===================== 路由生成 =====================

    /** 前端生成 */
    async function generateRoutesAtFront(asyncRoutes: Route.recordMainRaw[]) {
      routesRaw.value = cloneDeep(asyncRoutes) as any
      if (settingsStore.settings.app.enablePermission) {
        await userStore.getPermissions()
      }
      isGenerate.value = true
    }

    /** 后端路由数据格式化（旧版，views glob 方式） */
    function formatBackRoutes(routes: any, views = import.meta.glob('../../views/**/*.vue')): Route.recordMainRaw[] {
      return routes.map((route: any) => {
        switch (route.component) {
          case 'Layout':
            route.component = () => import('@/layouts/index.vue')
            break
          default:
            if (route.component) {
              route.component = views[`../../views/${route.component}`]
            } else {
              delete route.component
            }
        }
        if (route.children) {
          route.children = formatBackRoutes(route.children, views)
        }
        return route
      })
    }

    /** 处理单个路由项的通用属性（meta 解析、link、hidden 等） */
    function applyRouteDefaults(route: any) {
      route.meta = isString(route.meta) ? JSON.parse(route.meta) : route.meta
      route.meta.cache = false
      route.meta.hidden = route.hidden ?? false
      route.alwaysShow = route.alwaysShow ?? false
      route.redirect = (route.children?.length === 0) ? '' : (route.redirect ?? '')

      if (route.menu_type === 1) {
        const url = route.path.includes('https') ? route.path : `https:${route.path}`
        route.path = url
        route.meta.link = url
      }
    }

    /** 递归处理后端菜单数据（componentsMap 方式） */
    function formatNestedRoutes(routes: any): Route.recordMainRaw[] {
      return routes.map((route: any) => {
        if (route.component === 'layout') {
          route.component = () => import('@/layouts/index.vue')
        } else if (route.component) {
          route.component = (componentsMap as any)[route.component]
        } else {
          delete route.component
        }

        applyRouteDefaults(route)

        if (route.children?.length > 0) {
          route.children = formatNestedRoutes(route.children)
        }
        return route
      })
    }

    /** 后端获取 */
    async function generateRoutesAtBack() {
      const TMP_ROUTES = [{
        meta: { title: 'MIS', icon: 'uim:box' },
        children: [] as any[],
      }]
      try {
        const res = await getMenuList()
        const tmpMenus = cloneDeep(res.data.menus)
        TMP_ROUTES[0].children = TMP_ROUTES[0].children.concat(formatNestedRoutes(tmpMenus))
        constantRoutes.forEach(item => {
          TMP_ROUTES[0].children.unshift(item)
        })
        routesRaw.value = TMP_ROUTES
        if (settingsStore.settings.app.enablePermission) {
          await userStore.getPermissions()
        }
        isGenerate.value = true
      } catch {
        // 菜单获取失败时静默处理
      }
    }

    /** 文件系统生成 */
    async function generateRoutesAtFilesystem(asyncRoutes: RouteRecordRaw[]) {
      filesystemRoutesRaw.value = cloneDeep(asyncRoutes) as any
      if (settingsStore.settings.app.enablePermission) {
        await userStore.getPermissions()
      }
      isGenerate.value = true
    }

    function setCurrentRemoveRoutes(routes: (() => void)[]) {
      currentRemoveRoutes.value = routes
    }

    function removeRoutes() {
      isGenerate.value = false
    }

    return {
      isGenerate,
      routes,
      currentRemoveRoutes,
      flatRoutes,
      flatSystemRoutes,
      generateRoutesAtFront,
      generateRoutesAtBack,
      generateRoutesAtFilesystem,
      setCurrentRemoveRoutes,
      removeRoutes,
    }
  },
)

export default useRouteStore
// AI-GEN-END
